import { useEffect, useRef, useState } from "react";
import type { Booking } from "../../types/booking";
import { analyzeStoredCommercialInvoice } from "./commercialInvoiceAnalysisService";
import {
  bookingNeedsCiAnalysis,
  bookingNeedsCiDeepCheck,
  bookingNeedsAnyCiCheck,
} from "../../utils/agentBookingCiAnalysis";
import {
  CI_DUAL_CHECK_IDLE,
  isCiDualCheckActive,
  type CiDualCheckRunState,
} from "./ciDualCheckRunState";
import { getAnalysisResultFromBookingEmbed } from "./commercialInvoiceValidity";

const inFlightKeys = new Set<string>();

interface UseAutoCommercialInvoiceAnalysisOptions {
  /** When true, may trigger analysis if missing. */
  enabled: boolean;
  booking: Booking;
  objectPath: string | null | undefined;
  onUpdated?: () => void | Promise<void>;
}

const initialRunStateForBooking = (
  booking: Booking,
  needsHeuristic: boolean,
  needsDeepCheck: boolean
): CiDualCheckRunState => ({
  heuristic: needsHeuristic ? "running" : "done",
  deepCheck: needsDeepCheck ? (needsHeuristic ? "pending" : "running") : "done",
  liveResult: getAnalysisResultFromBookingEmbed(booking),
});

/** Runs heuristic + AI deep check on General tab when either is missing; progressive UI via runState. */
export function useAutoCommercialInvoiceAnalysis({
  enabled,
  booking,
  objectPath,
  onUpdated,
}: UseAutoCommercialInvoiceAnalysisOptions): { runState: CiDualCheckRunState } {
  const [runState, setRunState] = useState<CiDualCheckRunState>(CI_DUAL_CHECK_IDLE);
  const onUpdatedRef = useRef(onUpdated);
  const runGenerationRef = useRef(0);
  onUpdatedRef.current = onUpdated;

  useEffect(() => {
    if (!enabled) {
      runGenerationRef.current += 1;
      setRunState(CI_DUAL_CHECK_IDLE);
      return;
    }

    if (!objectPath) {
      setRunState(CI_DUAL_CHECK_IDLE);
      return;
    }

    if (!bookingNeedsAnyCiCheck(booking)) {
      if (!inFlightKeys.has(`${booking.id}:${objectPath}:dual`)) {
        setRunState(CI_DUAL_CHECK_IDLE);
      }
      return;
    }

    const needsHeuristic = bookingNeedsCiAnalysis(booking);
    const needsDeepCheck = needsHeuristic || bookingNeedsCiDeepCheck(booking);
    const runKey = `${booking.id}:${objectPath}:dual`;

    if (inFlightKeys.has(runKey)) {
      setRunState((prev) =>
        isCiDualCheckActive(prev)
          ? prev
          : initialRunStateForBooking(booking, needsHeuristic, needsDeepCheck)
      );
      return;
    }

    const generation = runGenerationRef.current;
    inFlightKeys.add(runKey);
    setRunState(initialRunStateForBooking(booking, needsHeuristic, needsDeepCheck));

    void (async () => {
      const isStale = () => generation !== runGenerationRef.current;
      let merged = getAnalysisResultFromBookingEmbed(booking);

      try {
        if (needsHeuristic) {
          const { data, error } = await analyzeStoredCommercialInvoice({
            bookingId: booking.id,
            objectPath,
          });

          if (isStale()) return;

          if (error || !data) {
            setRunState((prev) => ({
              ...prev,
              heuristic: "error",
              deepCheck: "idle",
              heuristicError: error ?? "Automatic check failed",
            }));
            return;
          }

          merged = data;
          setRunState((prev) => ({
            ...prev,
            heuristic: "done",
            deepCheck: needsDeepCheck ? "running" : "done",
            liveResult: merged,
          }));
          void onUpdatedRef.current?.();
        }

        if (needsDeepCheck) {
          if (!needsHeuristic) {
            setRunState((prev) => ({
              ...prev,
              deepCheck: "running",
            }));
          }

          const { data, error } = await analyzeStoredCommercialInvoice(
            { bookingId: booking.id, objectPath },
            { deepCheck: true }
          );

          if (isStale()) return;

          if (error || !data) {
            setRunState((prev) => ({
              ...prev,
              deepCheck: "error",
              deepCheckError: error ?? "AI deep check failed",
              liveResult: merged ?? prev.liveResult,
            }));
            void onUpdatedRef.current?.();
            return;
          }

          merged = data;

          if (!data.deepCheck?.available) {
            setRunState({
              heuristic: "done",
              deepCheck: "error",
              deepCheckError: data.deepCheck?.error ?? "AI deep check unavailable",
              liveResult: merged,
            });
            void onUpdatedRef.current?.();
            return;
          }

          setRunState({
            heuristic: "done",
            deepCheck: "done",
            liveResult: merged,
          });
          void onUpdatedRef.current?.();
        }
      } finally {
        inFlightKeys.delete(runKey);
      }
    })();
    // Omit booking embed from deps so refetch after automatic check does not restart/cancel AI.
  }, [enabled, booking.id, objectPath, booking.commercial_invoice_file]);

  return { runState };
}
